import React, { useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { crmErrorMessage } from "../api/crmErrors";
import { STAGES, STAGE_KEYS } from "../contract";
import CrmPopover from "../ui/CrmPopover";
import StageBadge from "../ui/StageBadge";
import CrmButton from "../ui/CrmButton";
import { FOCUS_RING } from "../ui/classes";
import { cn } from "../../../lib/utils";
import { CRM } from "../../../constants/testIds/crm";

/**
 * The stage badge on the Client Detail header (spec): set automatically by the stage
 * rules; a click allows a manual override, after which the badge shows "manual" and the
 * rules leave the client alone until "Resume automatic".
 */
export default function StageControl({ client, onSet, onResume }) {
  const t = useT("crm");
  const tc = useT("common");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const manual = client.stage_mode === "manual";

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); setOpen(false); } catch (e) { toast.error(crmErrorMessage(e, t, tc)); } finally { setBusy(false); }
  };

  return (
    <CrmPopover
      open={open}
      onOpenChange={setOpen}
      className="w-72 p-3"
      trigger={(
        <button type="button" data-testid={CRM.clientStage} aria-haspopup="dialog" aria-label={t("stage.change")}
          className={cn("rounded-full", FOCUS_RING)}>
          <StageBadge stage={client.stage} mode={client.stage_mode} />
        </button>
      )}
    >
      <p className="px-2 pb-2 text-xs text-eh-muted">{manual ? t("stage.manualHint") : t("stage.autoHint")}</p>
      <ul role="listbox" aria-label={t("stage.change")} className="space-y-1">
        {STAGES.map((s) => (
          <li key={s}>
            <button type="button" role="option" aria-selected={client.stage === s} disabled={busy}
              onClick={() => run(() => onSet(s))} data-testid={CRM.clientStageOption(s)}
              className={cn("w-full rounded-2xl px-3 py-2 text-left text-sm hover:bg-eh-cream", client.stage === s && "font-semibold", FOCUS_RING)}>
              {t(STAGE_KEYS[s])}
            </button>
          </li>
        ))}
      </ul>
      {manual && (
        <CrmButton variant="link" size="sm" className="mt-2 px-2" disabled={busy} onClick={() => run(onResume)} data-testid={CRM.clientStageResume}>
          {t("stage.resume")}
        </CrmButton>
      )}
    </CrmPopover>
  );
}
